import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import { buildStompAuthHeaders, canOpenStompConnection } from "@/shared/lib/stompAuth";
import { ticketedSockJs } from "@/shared/api/wsTicket";

const isTauri =
    typeof window !== "undefined" && !!(window as unknown as Record<string, unknown>).__TAURI_INTERNALS__;

// Último recurso aponta para PRODUÇÃO: staging DEVE definir NEXT_PUBLIC_NOTIFICATION_URL.
const defaultNotificationWsOrigin =
    "https://7days-gateway-production.up.railway.app";

const envNotificationWsOrigin = process.env.NEXT_PUBLIC_NOTIFICATION_URL?.replace(
    /\/api\/notification\/?$/,
    ""
);
if (!envNotificationWsOrigin && isTauri && process.env.NODE_ENV === "production") {
    console.warn(
        "[notificationWebSocket] NEXT_PUBLIC_NOTIFICATION_URL ausente; usando fallback de PRODUÇÃO:",
        defaultNotificationWsOrigin
    );
}

// Backend endpoint: /api/notification/ws (SockJS) — roteado pelo nginx do host (prod) e pelo proxy do Vite (dev).
const absoluteWsUrl = `${
    envNotificationWsOrigin || defaultNotificationWsOrigin
}/api/notification/ws`;

const NOTIFICATION_WS_ENDPOINT =
    isTauri && process.env.NODE_ENV === "production"
        ? absoluteWsUrl
        : "/api/notification/ws";

export interface NotificationWsMessage {
    id: string;
    title: string;
    message: string;
    type?: string;
    read: boolean;
    createdAt: string;
    link?: string;
    [key: string]: unknown;
}

class NotificationWebSocketService {
    private client: Client;
    private subscriptions: Map<string, StompSubscription> = new Map();
    private onConnectCallback?: () => void;
    private onDisconnectCallback?: () => void;

    constructor() {
        this.client = new Client({
            // O notification-service ainda não aceita o ticket: o JWT no CONNECT é quem autentica.
            ...ticketedSockJs(NOTIFICATION_WS_ENDPOINT),
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            connectHeaders: buildStompAuthHeaders(),
        });

        this.client.onConnect = () => {
            this.onConnectCallback?.();
        };

        this.client.onStompError = (frame) => {
            console.error("[NotificationWS] STOMP error:", frame.headers["message"]);
            // Reconectar com a mesma credencial recusada só gera loop de 5s no servidor.
            this.client.deactivate();
        };

        this.client.onWebSocketClose = () => {
            this.onDisconnectCallback?.();
        };
    }

    connect(onConnect?: () => void, onDisconnect?: () => void) {
        this.onConnectCallback = onConnect;
        this.onDisconnectCallback = onDisconnect;

        if (!canOpenStompConnection()) {
            console.warn("[NotificationWS] Sem token válido; ligação adiada.");
            return;
        }

        this.client.connectHeaders = buildStompAuthHeaders();
        this.client.activate();
    }

    disconnect() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions.clear();
        this.client.deactivate();
    }

    subscribeToUserNotifications(
        callback: (notification: NotificationWsMessage) => void
    ) {
        // Backend uses convertAndSendToUser → client subscribes to /user/queue/notifications
        const topic = "/user/queue/notifications";
        if (this.subscriptions.has(topic)) return;

        const sub = this.client.subscribe(topic, (message: IMessage) => {
            try {
                const payload: NotificationWsMessage = JSON.parse(message.body);
                callback(payload);
            } catch {
                console.error("[NotificationWS] Failed to parse message:", message.body);
            }
        });

        this.subscriptions.set(topic, sub);
    }

    unsubscribeFromUserNotifications() {
        const topic = "/user/queue/notifications";
        const sub = this.subscriptions.get(topic);
        if (sub) {
            sub.unsubscribe();
            this.subscriptions.delete(topic);
        }
    }
}

export const notificationWebSocket = new NotificationWebSocketService();
